import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface RichTextEditorProps {
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  alternate?: boolean;
  maxHeight?: string;
  placeholder?: string;
  id?: string;
}

export const RichTextEditor = forwardRef<HTMLDivElement, RichTextEditorProps>(
  ({ onKeyDown, alternate, maxHeight, placeholder, id }, ref) => {
    const { t } = useTranslation();
    // Clear leftover empty HTML so the placeholder shows again
    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      if (
        !e.currentTarget.textContent ||
        e.currentTarget.textContent.trim() === ''
      ) {
        e.currentTarget.innerHTML = '';
      }
    };
    // Seed a wrapper on first focus so the first line is wrapped in
    // <div> like subsequent lines, rather than sitting as a bare text node.
    const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (el.innerHTML) return;
      el.innerHTML = '<div><br></div>';
      const wrapper = el.firstChild;
      if (!wrapper) return;
      const range = document.createRange();
      range.setStart(wrapper, 0);
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    };
    return (
      <StyledEditor
        ref={ref}
        id={id}
        contentEditable
        $alternate={alternate}
        $maxHeight={maxHeight}
        data-placeholder={placeholder ?? t('wbs.type-message')}
        onKeyDown={onKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

const StyledEditor = styled.div<{ $alternate?: boolean; $maxHeight?: string }>`
  border: ${({ $alternate }) =>
    $alternate ? '1px solid var(--grey-300)' : 'none'};
  border-radius: ${({ $alternate }) => ($alternate ? '6px' : '0')};
  box-sizing: border-box;
  color: var(--grey-1000);
  font-family: var(--raleway);
  font-size: 14px;
  line-height: 1.3;
  max-height: ${({ $maxHeight }) => $maxHeight ?? '140px'};
  min-height: 80px;
  overflow-y: auto;
  padding: ${({ $alternate }) =>
    $alternate ? '30px 0 16px 10px' : '20px 0 16px 20px'};
  position: relative;
  width: 100%;

  &:focus {
    border-color: var(--blue);
    outline: none;
  }

  &:empty:before {
    color: ${({ $alternate }) =>
      $alternate ? 'var(--grey-500)' : 'var(--grey-600)'};
    content: attr(data-placeholder);
    font-family: ${({ $alternate }) =>
      $alternate ? 'var(--open-sans)' : 'var(--raleway)'};
    font-size: ${({ $alternate }) => ($alternate ? '16px' : '13px')};
    left: ${({ $alternate }) => ($alternate ? '10px' : '20px')};
    opacity: 1;
    pointer-events: none;
    position: absolute;
    top: ${({ $alternate }) => ($alternate ? '31px' : '20px')};
    transition: opacity 0.15s;
  }
  &:focus:empty:before {
    opacity: 0;
  }
  > *:first-child {
    margin-top: 0;
  }
  div {
    margin: 0;
  }
  p {
    margin: 0;
  }
  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  blockquote {
    border-left: ${({ $alternate }) =>
      $alternate ? '3px solid var(--grey-400)' : '1px solid var(--wbs-red)'};
    color: var(--grey-700);
    font-style: ${({ $alternate }) => ($alternate ? 'normal' : 'italic')};
    line-height: ${({ $alternate }) => ($alternate ? 'normal' : '4')};
    margin: ${({ $alternate }) => ($alternate ? '8px 0' : '8px 0 8px 16px')};
    padding-left: ${({ $alternate }) => ($alternate ? '12px' : '16px')};
  }

  a {
    color: var(--wbs-red);
    text-decoration: underline;
  }
`;
